/* eslint-disable max-lines */
import {
  Checkbox,
  CloseButton,
  Modal,
  Radio,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/elements/Button';
import ImagePicker from '../../components/elements/ImagePicker';
import LoadingBars from '../../components/elements/LoadingBars';
import ChipMultiSelect from '../../components/fields/ChipMultiSelect';
import TextArea from '../../components/fields/TextArea';
import InfoNotice from './InfoNotice';
import styles from './styles.scoped.css';

const ACCEPTED_FILES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const normalizeProvince = (province) => {
  if (typeof province === 'string') {
    return {
      label: province,
      name: province,
      value: province,
    };
  }

  return {
    label: province?.province || province?.provinceName || province?.name || '-',
    name: province?.province || province?.provinceName || province?.name || '-',
    value:
      province?.provinceId ||
      province?.id ||
      province?.value ||
      province?.province ||
      '',
  };
};

function RequiredLabel({ children }) {
  return (
    <label className={styles.sectionLabel}>
      {children} <span className={styles.required}>*</span>
    </label>
  );
}

RequiredLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

function HelperInfoText({ children }) {
  return (
    <div className={styles.helperText}>
      <span aria-hidden="true" className={styles.helperTextIcon}>
        <svg
          fill="none"
          height="14"
          viewBox="0 0 14 14"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="7" cy="7" fill="#6B7280" r="6" />
          <path
            d="M7 6V9.25"
            stroke="white"
            strokeLinecap="round"
            strokeWidth="1.25"
          />
          <circle cx="7" cy="4.25" fill="white" r="0.75" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

HelperInfoText.propTypes = {
  children: PropTypes.node.isRequired,
};

function ModalHeader({ onClose, title }) {
  return (
    <div className={styles.header}>
      <h4>{title}</h4>
      <span onClick={onClose}>
        <CloseButton />
      </span>
    </div>
  );
}

ModalHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default function ProvinceTransactionRestrictionModal({
  customerName,
  onClose,
  onSubmit,
  opened,
  provinces,
  provinceOptions,
  submitLoading,
}) {
  const normalizedProvinces = useMemo(
    () => (provinces || []).map(normalizeProvince).filter((item) => item.label),
    [provinces]
  );
  const provinceCount = normalizedProvinces.length;
  const currentProvinceIds = useMemo(
    () =>
      normalizedProvinces
        .map((item) => item.value)
        .filter((value) => value !== '' && value !== null && value !== undefined),
    [normalizedProvinces]
  );
  const provinceNames = normalizedProvinces.map((item) => item.label);
  const provinceText =
    provinceNames.length > 1
      ? `${provinceNames.slice(0, -1).join(', ')} & ${provinceNames[provinceNames.length - 1]}`
      : provinceNames[0] || '';

  const [actionType, setActionType] = useState(
    provinceCount === 0 ? 'whitelist' : 'delete'
  );
  const [reason, setReason] = useState('');
  const [selectedProvinceIds, setSelectedProvinceIds] = useState([]);
  const [selectedDeleteProvinceIds, setSelectedDeleteProvinceIds] = useState([]);
  const [supportDocument, setSupportDocument] = useState([]);
  const [agree, setAgree] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  useEffect(() => {
    if (opened) {
      setActionType(provinceCount === 0 ? 'whitelist' : 'delete');
      setReason('');
      setSelectedProvinceIds([]);
      setSelectedDeleteProvinceIds([]);
      setSupportDocument([]);
      setAgree(false);
      setSubmitAttempted(false);
      setFileErrorMessage('');
    }
  }, [opened, provinceCount, currentProvinceIds]);

  const isDeleteAction = actionType === 'delete';
  const showActionChoice = provinceCount > 0;
  const showWhitelistSelector = provinceCount === 0 || !isDeleteAction;

  const modalTitle =
    provinceCount === 0 ? 'Set Provinsi Transaksi' : 'Hapus/Set Provinsi Transaksi';
  const modalDescription =
    provinceCount === 0
      ? 'Apakah Anda yakin untuk set provinsi transaksi pelanggan?'
      : 'Apakah Anda yakin untuk hapus/set provinsi transaksi pelanggan?';
  const reasonLabel =
    provinceCount === 0
      ? 'Alasan Set Provinsi Transaksi Pelanggan'
      : 'Alasan Hapus/Set Provinsi Transaksi Pelanggan';
  const reasonPlaceholder =
    provinceCount === 0
      ? 'Tulis alasan set provinsi transaksi pelanggan'
      : 'Tulis alasan hapus/set provinsi transaksi pelanggan';
  const confirmationLabel =
    provinceCount === 0
      ? 'Saya yakin untuk set provinsi transaksi pelanggan ini'
      : 'Saya yakin untuk hapus/set provinsi transaksi pelanggan ini';

  const submitButtonLabel = (() => {
    if (provinceCount === 0) {
      return 'Set Provinsi Transaksi';
    }
    if (provinceCount > 1 && isDeleteAction) {
      return 'Hapus Provinsi Transaksi';
    }
    return 'Hapus/Set Provinsi Transaksi';
  })();

  const deleteProvinceString =
    provinceCount <= 1
      ? currentProvinceIds.join(',')
      : selectedDeleteProvinceIds.join(',');
  const whitelistProvinceString = selectedProvinceIds.join(',');

  const reasonError = submitAttempted && !reason ? 'Alasan wajib diisi' : '';
  const whitelistError =
    submitAttempted && showWhitelistSelector && selectedProvinceIds.length < 1
      ? 'Provinsi wajib dipilih'
      : '';
  const deleteProvinceError =
    submitAttempted && isDeleteAction && selectedDeleteProvinceIds.length < 1
      ? 'Provinsi yang akan dihapus belum dipilih'
      : '';
  const agreeError = submitAttempted && !agree ? 'Mohon konfirmasi tindakan ini' : '';
  const fileError =
    submitAttempted && supportDocument.length < 1 && !fileErrorMessage
      ? 'Dokumen pendukung wajib diunggah'
      : fileErrorMessage;

  const isSubmitDisabled =
    submitLoading ||
    !reason ||
    !agree ||
    supportDocument.length < 1 ||
    (showWhitelistSelector && selectedProvinceIds.length < 1) ||
    (isDeleteAction && selectedDeleteProvinceIds.length < 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (isSubmitDisabled) {
      return;
    }

    const formData = new FormData();
    formData.append('nationalityId', customerName.nationalityIdPlan || '');
    formData.append('yearMonth', dayjs().format('YYYY-MM'));
    formData.append(
      'provinceIds',
      isDeleteAction ? deleteProvinceString : whitelistProvinceString
    );
    formData.append('supportDocument', supportDocument[0]);
    formData.append('reason', reason);
    formData.append('actionType', isDeleteAction ? 'delete' : 'whitelist');

    onSubmit(formData, isDeleteAction ? 'delete' : 'whitelist');
  };

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      scrollAreaComponent={ScrollArea.Autosize}
      size="lg"
      withCloseButton={false}
    >
      <form className={styles.modalContent} onSubmit={handleSubmit}>
        <ModalHeader onClose={onClose} title={modalTitle} />

        <Text className={styles.modalDescription}>{modalDescription}</Text>

        {provinceCount > 0 && (
          <InfoNotice
            prefixText="Transaksi LPG 3 kg pelanggan telah tercatat di provinsi"
            provinceText={provinceText}
          />
        )}

        <div className={styles.fieldBlock}>
          <TextArea
            className={styles.textAreaField}
            helperText="Maksimal 300 karakter"
            input={{
              value: reason,
              onChange: (event) => setReason(event.target.value),
            }}
            inputProps={{
              label: reasonLabel,
              placeholder: reasonPlaceholder,
              rows: 4,
            }}
            maxLength={300}
            meta={{
              touched: submitAttempted,
              error: reasonError,
            }}
            required
          />
        </div>

        <div className={styles.fieldBlock}>
          <RequiredLabel>Atur Provinsi Transaksi Pelanggan</RequiredLabel>
          {showActionChoice ? (
            <>
              <Text className={styles.sectionHint}>
                Bebaskan pelanggan bertransaksi di 2 provinsi atau tetapkan
                provinsi tertentu untuk transaksi bulan ini.
              </Text>
              <button
                className={[
                  styles.actionTypeCard,
                  isDeleteAction ? styles.actionTypeCardSelected : '',
                  deleteProvinceError ? styles.actionTypeCardError : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setActionType('delete')}
                type="button"
              >
                <div className={styles.actionTypeHeader}>
                  <Radio
                    checked={isDeleteAction}
                    classNames={{ radio: styles.radioInput }}
                    onChange={() => setActionType('delete')}
                    value="delete"
                  />
                  <Text className={styles.actionTypeLabel}>
                    Hapus provinsi transaksi, hapus catatan provinsi saat ini
                  </Text>
                </div>

                {isDeleteAction && (
                  <div className={styles.deleteProvincePanel}>
                    <Text className={styles.deleteProvinceLabel}>
                      Pilih provinsi yang ingin dihapus:
                    </Text>
                    <Checkbox.Group
                      onChange={setSelectedDeleteProvinceIds}
                      value={selectedDeleteProvinceIds}
                    >
                      <Stack gap="xs" mt="sm">
                        {normalizedProvinces.map((province) => (
                          <Checkbox
                            classNames={{ root: styles.checkboxItem }}
                            key={province.value || province.label}
                            label={province.label}
                            value={province.value}
                          />
                        ))}
                      </Stack>
                    </Checkbox.Group>
                  </div>
                )}
              </button>

              <button
                className={[
                  styles.actionTypeCard,
                  actionType === 'whitelist' ? styles.actionTypeCardSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setActionType('whitelist')}
                type="button"
              >
                <div className={styles.actionTypeHeader}>
                  <Radio
                    checked={actionType === 'whitelist'}
                    classNames={{ radio: styles.radioInput }}
                    onChange={() => setActionType('whitelist')}
                    value="whitelist"
                  />
                  <Text className={styles.actionTypeLabel}>
                    Tetapkan provinsi yang diizinkan untuk transaksi bulan ini
                  </Text>
                </div>
              </button>
              {deleteProvinceError && isDeleteAction && (
                <span className={styles.errorText}>{deleteProvinceError}</span>
              )}
            </>
          ) : (
            <>
              <ChipMultiSelect
                error={Boolean(whitelistError)}
                maxValues={2}
                onChange={setSelectedProvinceIds}
                options={provinceOptions}
                placeholder="Pilih Provinsi"
                value={selectedProvinceIds}
              />
              <HelperInfoText>Anda hanya bisa memilih 2 Provinsi.</HelperInfoText>
              {whitelistError && <span className={styles.errorText}>{whitelistError}</span>}
            </>
          )}
        </div>

        {showWhitelistSelector && showActionChoice && (
          <div className={`${styles.fieldBlock} ${styles.optionCard}`}>
            <RequiredLabel>Pilih provinsi tertentu pelanggan bertransaksi</RequiredLabel>
            <ChipMultiSelect
              error={Boolean(whitelistError)}
              maxValues={2}
              onChange={setSelectedProvinceIds}
              options={provinceOptions}
              placeholder="Pilih Provinsi"
              value={selectedProvinceIds}
            />
            <HelperInfoText>Anda hanya bisa memilih 2 Provinsi.</HelperInfoText>
            {whitelistError && <span className={styles.errorText}>{whitelistError}</span>}
          </div>
        )}

        <div className={`${styles.fieldBlock} ${styles.uploadField}`}>
          <ImagePicker
            accept={ACCEPTED_FILES}
            errorMessage={fileError}
            info="Format file .doc, .docx atau .pdf dan tidak lebih dari 2 Mb"
            maxSize={2000000}
            onSelect={setSupportDocument}
            required
            setErrorMessage={setFileErrorMessage}
            title="Dokumen Pendukung"
          />
          {fileError && <span className={styles.errorText}>{fileError}</span>}
        </div>

        <div className={styles.fieldBlock}>
          <Checkbox
            checked={agree}
            label={confirmationLabel}
            onChange={(event) => setAgree(event.currentTarget.checked)}
            styles={{
              body: {
                alignItems: 'center',
              },
              label: {
                color: '#25282B',
                fontSize: '0.875rem',
                fontWeight: 400,
              },
            }}
          />
          {agreeError && <span className={styles.errorText}>{agreeError}</span>}
        </div>

        <div className={styles.modalActions}>
          <Button
            className={styles.modalActionButton}
            fullWidth
            onClick={onClose}
            style="outlined"
            type="button"
          >
            Batal
          </Button>
          <Button
            className={styles.modalActionButton}
            disabled={isSubmitDisabled}
            fullWidth
            style="blue"
            type="submit"
          >
            {submitLoading && <LoadingBars size={1} />}
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

ProvinceTransactionRestrictionModal.defaultProps = {
  customerName: {},
  onClose: () => {},
  onSubmit: () => {},
  opened: false,
  provinceOptions: [],
  provinces: [],
  submitLoading: false,
};

ProvinceTransactionRestrictionModal.propTypes = {
  customerName: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  opened: PropTypes.bool,
  provinceOptions: PropTypes.array,
  provinces: PropTypes.array,
  submitLoading: PropTypes.bool,
};
